import { SelectField } from "@/components/SelectField";
import { SensitiveField } from "@/components/SensitiveField";
import { ACCOUNT_TYPE_OPTIONS } from "@/lib/labels";
import type { BankForm, FieldSetter } from "./types";
import { FieldWrap } from "./FieldWrap";
import { SameAsToggle } from "./SameAsToggle";
import { TF } from "./TF";

export function BankingFields({
  deposit,
  setDepositField,
  withdrawal,
  setWithdrawalField,
  withdrawalSame,
  setWithdrawalSame,
  fieldError,
  required,
  hideSensitive = false,
}: {
  deposit: BankForm;
  setDepositField: FieldSetter<BankForm>;
  withdrawal: BankForm;
  setWithdrawalField: FieldSetter<BankForm>;
  withdrawalSame: boolean;
  setWithdrawalSame: (v: boolean) => void;
  fieldError: (field: string) => string | undefined;
  required: boolean;
  // Skips account/routing numbers -- redacted to a masked last-4 at
  // write time (see sharedModules.redact_merchant_for_storage), so
  // there's never a raw value on file to prefill or safely overwrite.
  // Account type and bank name are NOT redacted and stay editable.
  hideSensitive?: boolean;
}) {
  return (
    <>
      <p className="form-subsection-title">Deposit account</p>
      {!hideSensitive && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FieldWrap
            id="dep-acct"
            label="Account number"
            sensitive
            required={required}
            error={fieldError("dep-acct")}
          >
            <SensitiveField
              id="dep-acct"
              value={deposit.accountNumber}
              onChange={(v) =>
                setDepositField(
                  "accountNumber",
                  v.replace(/\D/g, "").slice(0, 17),
                )
              }
              maxLength={17}
              inputMode="numeric"
              ariaInvalid={!!fieldError("dep-acct")}
              ariaDescribedBy={
                fieldError("dep-acct") ? "dep-acct-err" : undefined
              }
            />
          </FieldWrap>
          <FieldWrap
            id="dep-acct-confirm"
            label="Confirm account number"
            sensitive
            required={required}
            error={fieldError("dep-acct-confirm")}
          >
            <SensitiveField
              id="dep-acct-confirm"
              value={deposit.confirmAccountNumber}
              onChange={(v) =>
                setDepositField(
                  "confirmAccountNumber",
                  v.replace(/\D/g, "").slice(0, 17),
                )
              }
              maxLength={17}
              inputMode="numeric"
              placeholder="Re-enter to confirm"
              ariaInvalid={!!fieldError("dep-acct-confirm")}
              ariaDescribedBy={
                fieldError("dep-acct-confirm")
                  ? "dep-acct-confirm-err"
                  : undefined
              }
            />
          </FieldWrap>
          <FieldWrap
            id="dep-routing"
            label="Routing number"
            sensitive
            required={required}
            error={fieldError("dep-routing")}
          >
            <SensitiveField
              id="dep-routing"
              value={deposit.routingNumber}
              onChange={(v) =>
                setDepositField(
                  "routingNumber",
                  v.replace(/\D/g, "").slice(0, 9),
                )
              }
              maxLength={9}
              inputMode="numeric"
              placeholder="9-digit ABA number"
              ariaInvalid={!!fieldError("dep-routing")}
              ariaDescribedBy={
                fieldError("dep-routing") ? "dep-routing-err" : undefined
              }
            />
          </FieldWrap>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FieldWrap
          id="dep-type"
          label="Account type"
          required={required}
          error={fieldError("dep-type")}
        >
          <SelectField
            id="dep-type"
            value={deposit.accountType}
            options={ACCOUNT_TYPE_OPTIONS}
            onChange={(v) => setDepositField("accountType", v)}
            ariaInvalid={!!fieldError("dep-type")}
            ariaDescribedBy={
              fieldError("dep-type") ? "dep-type-err" : undefined
            }
          />
        </FieldWrap>
        <TF
          id="dep-bank"
          label="Bank name"
          value={deposit.bankName}
          onChange={(v) => setDepositField("bankName", v)}
          maxLength={200}
          required={required}
          error={fieldError("dep-bank")}
        />
      </div>
      <SameAsToggle
        checked={withdrawalSame}
        onChange={setWithdrawalSame}
        label="Withdrawal account is the same as the deposit account"
        subtitle="Withdrawal account"
      >
        <>
          {!hideSensitive && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FieldWrap
                id="wd-acct"
                label="Account number"
                sensitive
                required={required}
                error={fieldError("wd-acct")}
              >
                <SensitiveField
                  id="wd-acct"
                  value={withdrawal.accountNumber}
                  onChange={(v) =>
                    setWithdrawalField(
                      "accountNumber",
                      v.replace(/\D/g, "").slice(0, 17),
                    )
                  }
                  maxLength={17}
                  inputMode="numeric"
                  ariaInvalid={!!fieldError("wd-acct")}
                  ariaDescribedBy={
                    fieldError("wd-acct") ? "wd-acct-err" : undefined
                  }
                />
              </FieldWrap>
              <FieldWrap
                id="wd-acct-confirm"
                label="Confirm account number"
                sensitive
                required={required}
                error={fieldError("wd-acct-confirm")}
              >
                <SensitiveField
                  id="wd-acct-confirm"
                  value={withdrawal.confirmAccountNumber}
                  onChange={(v) =>
                    setWithdrawalField(
                      "confirmAccountNumber",
                      v.replace(/\D/g, "").slice(0, 17),
                    )
                  }
                  maxLength={17}
                  inputMode="numeric"
                  placeholder="Re-enter to confirm"
                  ariaInvalid={!!fieldError("wd-acct-confirm")}
                  ariaDescribedBy={
                    fieldError("wd-acct-confirm")
                      ? "wd-acct-confirm-err"
                      : undefined
                  }
                />
              </FieldWrap>
              <FieldWrap
                id="wd-routing"
                label="Routing number"
                sensitive
                required={required}
                error={fieldError("wd-routing")}
              >
                <SensitiveField
                  id="wd-routing"
                  value={withdrawal.routingNumber}
                  onChange={(v) =>
                    setWithdrawalField(
                      "routingNumber",
                      v.replace(/\D/g, "").slice(0, 9),
                    )
                  }
                  maxLength={9}
                  inputMode="numeric"
                  placeholder="9-digit ABA number"
                  ariaInvalid={!!fieldError("wd-routing")}
                  ariaDescribedBy={
                    fieldError("wd-routing") ? "wd-routing-err" : undefined
                  }
                />
              </FieldWrap>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FieldWrap
              id="wd-type"
              label="Account type"
              required={required}
              error={fieldError("wd-type")}
            >
              <SelectField
                id="wd-type"
                value={withdrawal.accountType}
                options={ACCOUNT_TYPE_OPTIONS}
                onChange={(v) => setWithdrawalField("accountType", v)}
                ariaInvalid={!!fieldError("wd-type")}
                ariaDescribedBy={
                  fieldError("wd-type") ? "wd-type-err" : undefined
                }
              />
            </FieldWrap>
            <TF
              id="wd-bank"
              label="Bank name"
              value={withdrawal.bankName}
              onChange={(v) => setWithdrawalField("bankName", v)}
              maxLength={200}
              required={required}
              error={fieldError("wd-bank")}
            />
          </div>
        </>
      </SameAsToggle>
    </>
  );
}
